/** @import ../../../common/types.js */

import { produce } from 'immer'
import { BaseControl, BaseFilter, BaseOption, BasicFilterStore } from './common'
import MonthlyMonitoringFilter from './MonthlyMonitoringFilter.js'
import { formatCommaDigit, parseCommaDigit } from '../../../common/utils/NumberUtils.js'

const noSelectCertificationSetKey = 'no_select_certification_set'
const noSelectScoreCertificationKey = 'no_select_score_certification'

const certificationOptionKeyPrefix = 'certification_'
const subCategoryOptionPrefix = 'sub_'
const mainCategoryOptionPrefix = 'main_'

// カテゴリをカテゴリーに変換
const categoryLbl = (category, removeParam = true) => {
  const correction = category.replaceAll('カテゴリ', 'カテゴリー')

  if (removeParam) correction.replaceAll(/\(|\)/g, '')

  return correction
}

class TrainingResignationStore extends BasicFilterStore {}
export default class TrainingResignationFilter extends BaseFilter {
  /** @type {Group[]} */
  #groups = []
  /** @type {Map<string, Control>} */
  #controls = new Map()
  /** @type {Map<string, Option>} */
  #options = new Map()
  #result
  /** @type {number} */
  DepartmentHierarchyCount = 6
  /** @type {1 | 2 | 3} 設計の処理仕様での並び順パターン 左→右の順 */
  type = 1
  /** @type {Map<string, Option>} */
  certificationOptions = new Map()
  /** @type {Map<string, Option>} */
  certificationMainCategory = new Map()
  /** @type {Map<string, Option>} */
  certificationSubCategory = new Map()
  /** @type {Map<string, Option>} */
  certificationSets = new Map()
  hasScoreCertificationOptions = new Map()

  // Reduxの状態
  store = new TrainingResignationStore()

  /**
   * 月次フィルタの共有
   *
   * @param {any} result
   */
  async addMonthlyFilterControl(result) {
    const filter = new MonthlyMonitoringFilter(result)
    await filter.init()
    this.#options = filter.getConfig().options
  }

  /**
   * //メインカテゴリーをソート
   *
   * @param {any} CertificationMaster
   */
  prepareMainCategory(CertificationMaster = []) {
    CertificationMaster.forEach((e) => {
      const [mainCategory] = e
      const mainCategoryId = mainCategoryOptionPrefix + mainCategory

      const option = new BaseOption(mainCategoryId, categoryLbl(mainCategory, false), mainCategory)
      option.setState('value', this.type !== 3)

      option.set('onChange', (value) => {
        const optionState = produce(this.store.getStoreData().optionState, (optionMap) => {
          this.certificationSubCategory.forEach((e) => {
            if (e.meta.mainCategory === mainCategoryId) {
              optionMap.get(e.id).state.value = value
              optionMap.get(e.id).state.show = value
            } else {
              const mainCategory = optionMap.get(e.id).meta.mainCategory
              const mainCategoryValue = optionMap.get(mainCategory).state.value

              optionMap.get(e.id).state.value = mainCategoryValue
              optionMap.get(e.id).state.show = mainCategoryValue
            }
          })

          this.certificationOptions.forEach((e) => {
            if (e.meta.mainCategory === mainCategoryId) {
              optionMap.get(e.id).state.value = value
              optionMap.get(e.id).state.show = value
            } else {
              const mainCategory = optionMap.get(e.id).meta.mainCategory
              const mainCategoryValue = optionMap.get(mainCategory).state.value

              optionMap.get(e.id).state.value = mainCategoryValue
              optionMap.get(e.id).state.show = mainCategoryValue
            }
          })
        })

        this.store.overrideOptionState(optionState)
      })

      this.certificationMainCategory.set(mainCategoryId, option)
    })
  }

  /**
   * //サブカテゴリーをソート
   *
   * @param {any} CertificationMaster
   */
  prepareSubCategory(CertificationMaster = []) {
    CertificationMaster.forEach((e, index) => {
      const [mainCategory, subCategory] = e

      const mainCategoryId = mainCategoryOptionPrefix + mainCategory
      const subCategoryId = subCategoryOptionPrefix + subCategory + '_' + mainCategory

      const option = new BaseOption(subCategoryId, categoryLbl(subCategory, false), subCategory)
        .set('meta', {
          mainCategory: mainCategoryId,
          displayOrder: index
        })
        .set('extra', categoryLbl(mainCategory))

      option.setState('value', this.type !== 3)

      option.set('onChange', (value) => {
        const optionState = produce(this.store.getStoreData().optionState, (optionMap) => {
          this.certificationOptions.forEach((e) => {
            if (e.meta.subCategory === subCategoryId) {
              optionMap.get(e.id).state.value = value
              optionMap.get(e.id).state.show = value
            } else {
              const subCategory = optionMap.get(e.id).meta.subCategory
              const subCategoryValue = optionMap.get(subCategory).state.value

              optionMap.get(e.id).state.value = subCategoryValue
              optionMap.get(e.id).state.show = subCategoryValue
            }
          })
        })
        this.store.overrideOptionState(optionState)
      })

      this.certificationSubCategory.set(subCategoryId, option)
    })
  }

  /**
   * 資格の選択肢の作成
   *
   * @param {any} result
   */
  prepareCertification(result) {
    const { CertificationMaster = [] } = result
    // 0: メインカテゴリー , 1; サブカテゴリー , 2; 資格コード , 3: 資格名

    const master = CertificationMaster.sort((a, b) => {
      const aSort = a[4]
      const bSort = b[4]
      return Number(aSort) - Number(bSort)
    })

    this.prepareMainCategory(master)
    this.prepareSubCategory(master)

    master.forEach((e) => {
      const [mainCategory, subCategory, certificationCode, certificationName, displayOrder] = e
      const id = certificationOptionKeyPrefix + certificationCode // あとでフィルタをかけるため

      const mainCategoryId = mainCategoryOptionPrefix + mainCategory
      const subCategoryId = subCategoryOptionPrefix + subCategory + '_' + mainCategory

      const option = new BaseOption(id, certificationName, certificationName)
        .set('meta', {
          certificationCode,
          mainCategory: mainCategoryId,
          subCategory: subCategoryId,
          displayOrder
        })
        .set('extra', [categoryLbl(mainCategory), categoryLbl(subCategory)].join('>'))

      option.setState('value', this.type !== 3)

      this.certificationOptions.set(id, option)
    })

    this.addToOptions(this.certificationMainCategory)
    this.addToOptions(this.certificationSubCategory)
    this.addToOptions(this.certificationOptions)
  }

  /**
   * @function 点数有無資格の選択肢を作成
   * @param {any} result
   */
  prepareCertificationScore(result) {
    const { CertificationHasScoreList } = result

    const certificationHasScoreSet = new Set(CertificationHasScoreList)
    const options = new Map()

    // 点数有無資格用
    const noSelectedScoreCertificationOption = new BaseOption(noSelectScoreCertificationKey, '選択をクリア', '').set(
      'onChange',
      () => {
        if (document.getElementById('control_score')) document.getElementById('control_score').dataset.noFormat = ''
        this.store.updateOptionValue('score', '')
      }
    )

    noSelectedScoreCertificationOption.setState('value', true)
    noSelectedScoreCertificationOption.setState('showLabel', false)
    options.set(noSelectScoreCertificationKey, noSelectedScoreCertificationOption)

    for (const e of this.certificationOptions.values()) {
      if (!certificationHasScoreSet.has(e.label)) {
        continue
      }
      const { certificationId = '' } = e.meta
      const id = 'score_' + e.id
      const opt = new BaseOption(id, e.label, e.value)
        .set('meta', {
          controlId: SingleCertificationControl.C_KEY,
          certificationId
        })
        .set('extra', e.extra)

      opt.setState('value', false)

      options.set(id, opt)
    }

    this.addToOptions(options)

    this.hasScoreCertificationOptions = options
  }

  /**
   * 資格セットプルダウンの選択肢の作成ロジック
   *
   * @param {any} id
   * @param {any} certificationData
   * @param {function( any ):void} selectAllPredicate
   * @returns
   */
  createCertificationSetOption(id, certificationData) {
    const { CertificationSetName, CertificationCodeList } = certificationData
    const option = new BaseOption(id, CertificationSetName, CertificationSetName).set('meta', {
      codeList: CertificationCodeList
    })

    option.set('onChange', () => {
      const codeSet = new Set(CertificationCodeList.map((e) => `${certificationOptionKeyPrefix}${e}`))

      const optionState = produce(this.store.getStoreData().optionState, (optionMap) => {
        const selectAllPredicate = (e) => {
          optionMap.get(e).state.value = true
          optionMap.get(e).state.show = true
        }

        // メインカテゴリすべてを選択
        this.certificationMainCategory.keys().forEach(selectAllPredicate)
        // サブカテゴリすべてを選択
        this.certificationSubCategory.keys().forEach(selectAllPredicate)

        this.certificationOptions.keys().forEach((e) => {
          optionMap.get(e).state.value = codeSet.has(e)
          optionMap.get(e).state.show = true
          optionMap.get(e).state.showLabel = true
        })
      })

      this.store.overrideOptionState(optionState)
    })

    option.setState('value', false)

    return option
  }

  /**
   * 資格セットの作成
   *
   * @param {any} result
   */
  prepareCertificationSet(result) {
    const { CertificationSetList = [] } = result

    // 資格セット用
    const noSelectedCertificationSet = new BaseOption(noSelectCertificationSetKey, '選択をクリア', '').set(
      'onChange',
      () => {
        const optionState = produce(this.store.getStoreData().optionState, (draft) => {
          const selectAllPredicate = (e) => {
            draft.get(e).state.value = this.type !== 3
            draft.get(e).state.show = true
          }

          this.certificationMainCategory.keys().forEach(selectAllPredicate)
          this.certificationSubCategory.keys().forEach(selectAllPredicate)
          this.certificationOptions.keys().forEach(selectAllPredicate)
        })
        this.store.overrideOptionState(optionState)
      }
    )
    noSelectedCertificationSet.setState('value', true)
    noSelectedCertificationSet.setState('showLabel', false)

    this.certificationSets.set(noSelectCertificationSetKey, noSelectedCertificationSet)

    for (let index = 0; index < CertificationSetList.length; index++) {
      const id = 'certificationSetOption' + index
      const option = this.createCertificationSetOption(id, CertificationSetList[index])
      this.certificationSets.set(id, option)
    }

    this.addToOptions(this.certificationSets)
  }

  constructor(result, type = 1) {
    super()

    this.#result = result

    const { DepartmentHierarchyCount = 6 } = result

    this.DepartmentHierarchyCount = DepartmentHierarchyCount
    this.type = type
  }

  /** @param {Map<string, Option>} options */
  addToOptions(options = new Map()) {
    this.#options = produce(this.#options, (draft) => {
      options.forEach((v, k) => {
        draft.set(k, v)
      })
    })
  }

  /** @param {any} フィルターのグループを作成 */
  createGroups() {
    this.#groups.push({
      title: '',
      controlIdList: Object.keys(this.#controls),
      disabled: false,
      show: true
    })
  }

  /** 資格入力 */
  createCertificationScoreOptions() {
    const scoreOptionKey = 'score'
    const option = new BaseOption(scoreOptionKey, '', '')
      .set('valueClassName', 'text-right placeholder:text-left')
      .set('placeholder', '数値を入力してください')
      .set('meta', { controlId: CertificationScoreControl.C_KEY })
      .set('inputProps', {
        'data-no-format': '',
        onInput: (e) => {
          const value = e.target.value.trim().replaceAll(/\D+/g, '')
          e.target.dataset.noFormat = parseCommaDigit(value)
        },
        onBlur: (e) => {
          const currentTarget = e.target
          currentTarget.dataset.noFormat = parseCommaDigit(currentTarget.value)
          currentTarget.value = formatCommaDigit(currentTarget.dataset.noFormat)
        },
        onFocus: (e) => {
          const currentTarget = e.target
          currentTarget.dataset.noFormat = parseCommaDigit(e.target.value)
          currentTarget.value = parseCommaDigit(e.target.value)
        }
      })

    option.setState('value', '')
    option.set('onChange', (e, id) => {
      const value = e.target.dataset.noFormat.toString()
      this.store.updateOptionValue(id, value)
    })

    this.addToOptions(new Map([[scoreOptionKey, option]]))
  }

  /** @function createScoringAggregationOptions 点数集計方法選択肢の作成 */
  createScoringAggregationOptions() {
    const maxScoreOptionKey = 'max'
    const maxScoreOption = new BaseOption(maxScoreOptionKey, '最高点', maxScoreOptionKey)
      .set('meta', {
        controlId: ScoringAggregation.C_KEY
      })
      .set('state', {
        value: false,
        show: true
      })

    const latestScoreOptionKey = 'latest'
    const latestScoreOption = new BaseOption(latestScoreOptionKey, '最新点', latestScoreOptionKey)
      .set('meta', {
        controlId: ScoringAggregation.C_KEY
      })
      .set('state', {
        value: true,
        show: true
      })
    this.addToOptions(
      new Map([
        [latestScoreOptionKey, latestScoreOption],
        [maxScoreOptionKey, maxScoreOption]
      ])
    )
  }

  /** 残選択の作成 */
  createOptions() {
    const result = this.#result

    this.createDepartmentLevelOptions()

    this.prepareCertification(result)
    this.prepareCertificationSet(result)
    this.prepareCertificationScore(result)
    this.createCertificationScoreOptions()
    this.createScoringAggregationOptions()
  }

  /**
   * @function 組織階層の選択を作成
   * @returns {string[]}
   */
  createDepartmentLevelOptions() {
    const options = new Map()

    for (let i = 1; i <= this.DepartmentHierarchyCount; i++) {
      const key = `departmentLevel${i}`
      const option = new BaseOption(key, `階層${i}`, i).set('meta', {
        controlId: LayerLevelControl.C_KEY
      })

      option.set('onChange', (e) => {
        if (this.type !== 2) return

        const { controlState, optionState } = this.store.getStoreData()

        const newControlState = produce(controlState, (draft) => {
          draft.get(MultiDepartmentSelectControl.C_KEY).setOptionIdList(draft.get(`department${e}`).optionIdList)
        })

        const newOptionState = produce(optionState, (draft) => {
          draft.forEach((option, key) => {
            const controlId = option?.meta?.controlId

            if (/department(\d{1,2})/.test(controlId)) {
              draft.get(key).state.show = true
              draft.get(key).state.value = true
            }
          })
        })

        this.store.overrideControlState(newControlState)
        this.store.overrideOptionState(newOptionState)
      })
      if (i === 1) {
        option.setState('value', true)
      }

      options.set(key, option)
    }
    this.addToOptions(options)
  }

  /**
   * コントロールIDからオプションIDリスト
   *
   * @param {string} コントロールID
   */
  getOptionListFromControlId(id) {
    const optionIdList = []

    this.#options.forEach((e) => {
      if (e.meta.controlId === id) {
        optionIdList.push(e.id)
      }
    })
    return optionIdList
  }

  /** 組織階層選択 */
  createLayerLevelControl() {
    const key = LayerLevelControl.C_KEY
    const control = new LayerLevelControl()
    control.setOptionIdList(this.getOptionListFromControlId(control.id))
    this.#controls.set(key, control)
  }

  /** 階層の組織コントロールを作成 */
  createMultiDepartmentControl() {
    const key = MultiDepartmentControl.C_KEY

    for (let index = 1; index <= this.DepartmentHierarchyCount; index++) {
      const id = key + index
      const control = new MultiDepartmentControl(id)
      control.label = `階層${index}の組織を選択`

      control.setOptionIdList(this.getOptionListFromControlId(control.id))

      this.#controls.set(id, control)
    }
  }

  /** 複数組織選択、選択の階層に応じて選択変換する */
  createMultiSelectDepartmentControl() {
    const key = MultiDepartmentSelectControl.C_KEY
    const control = new MultiDepartmentSelectControl()

    control.setOptionIdList(this.getOptionListFromControlId('department1'))

    this.#controls.set(key, control)
  }

  /** 資格セットコントロール */
  createCertificationSetControl() {
    const key = CertificationSetControl.C_KEY
    const control = new CertificationSetControl()

    control.setOptionIdList(Array.from(this.certificationSets.keys()))
    this.#controls.set(key, control)
  }

  /** - メインカテゴリーコントロール */
  createMainCategoryControl() {
    const key = MainCategoryControl.C_KEY
    const control = new MainCategoryControl()

    control.setOptionIdList(Array.from(this.certificationMainCategory.keys()))

    this.#controls.set(key, control)
  }

  /** サブカテゴリーコントロール */
  createSubCategoryControl() {
    const key = SubCategoryControl.C_KEY
    const control = new SubCategoryControl()
    control.setOptionIdList(Array.from(this.certificationSubCategory.keys()))
    this.#controls.set(key, control)
  }

  /** 複数資格選択 */
  createMultiCertificationControl() {
    const key = MultiCertificationControl.C_KEY
    const type3Id = 'certList'

    const control = new MultiCertificationControl()
    if (this.type === 3) control.id = type3Id
    control.label = '資格を選択（複数選択)'

    control.setOptionIdList(Array.from(this.certificationOptions.keys()))

    this.#controls.set(control.id, control)
  }

  /** 単一の資格 */
  createSingleCertificationControl() {
    const key = SingleCertificationControl.C_KEY
    const control = new SingleCertificationControl()
    control.label = '資格を選択（単一選択）'
    control.setOptionIdList(Array.from(this.hasScoreCertificationOptions.keys()))

    this.#controls.set(key, control)
  }

  /** 資格選択点数 */
  createScoreControl() {
    const key = CertificationScoreControl.C_KEY
    const control = new CertificationScoreControl()

    control.setOptionIdList(['score'])

    this.#controls.set(key, control)
  }

  /** @function createScringAggregationControl 点数集計方法コントロールの作成 */
  createScoringAggregationControl() {
    const key = ScoringAggregation.C_KEY
    const control = new ScoringAggregation()
    control.setOptionIdList(['latest', 'max'])

    this.#controls.set(key, control)
  }

  createControls() {
    // 資格階層
    this.createLayerLevelControl()
    // 組織
    this.createMultiDepartmentControl()
    this.createMultiSelectDepartmentControl()

    // 資格
    this.createSingleCertificationControl()
    this.createMultiCertificationControl()
    // 資格セット
    this.createCertificationSetControl()
    // 資格カテゴリ
    this.createMainCategoryControl()
    this.createSubCategoryControl()
    // 点数
    this.createScoreControl()
    //最高点・最新点
    this.createScoringAggregationControl()
  }

  async init() {
    const result = this.#result

    await this.addMonthlyFilterControl(result)
    this.createOptions()

    this.createControls()
    this.createGroups()
  }

  getConfig() {
    return {
      options: this.#options,
      controls: this.#controls,
      groups: this.getGroup(),
      generateOutput: this.generateOutput
    }
  }

  getGroup() {
    if (this.type === 1) {
      const department = new Array(this.DepartmentHierarchyCount)
        .fill(null)
        .map((_, index) => MultiDepartmentControl.C_KEY + (index + 1))

      return [
        {
          title: '',
          show: true,
          disabled: false,
          hasBorder: true,
          controlIdList: [
            CertificationSetControl.C_KEY,
            MainCategoryControl.C_KEY,
            SubCategoryControl.C_KEY,
            MultiCertificationControl.C_KEY
          ]
        },
        {
          title: '',
          show: true,
          disabled: false,
          controlIdList: department
        }
      ]
    } else if (this.type === 2) {
      return [
        {
          title: '',
          show: true,
          disabled: false,
          hasBorder: true,
          controlIdList: [LayerLevelControl.C_KEY, MultiDepartmentSelectControl.C_KEY]
        },

        {
          title: '',
          show: true,
          disabled: false,
          controlIdList: [
            CertificationSetControl.C_KEY,
            MainCategoryControl.C_KEY,
            SubCategoryControl.C_KEY,
            MultiCertificationControl.C_KEY
          ]
        }
      ]
    } else if (this.type === 3) {
      return [
        {
          title: '保有資格 (複数選択可)',
          show: true,
          disabled: false,
          hasBorder: true,
          controlIdList: [
            CertificationSetControl.C_KEY,
            MainCategoryControl.C_KEY,
            SubCategoryControl.C_KEY,
            'certList'
          ]
        },
        {
          title: '点数分析資格（単一選択）',
          show: true,
          disabled: false,
          controlIdList: [SingleCertificationControl.C_KEY, CertificationScoreControl.C_KEY, ScoringAggregation.C_KEY]
        }
      ]
    } else return []
  }
}

/** 組織階層 */
class LayerLevelControl extends BaseControl {
  static C_KEY = 'layerLevel'

  constructor(...args) {
    super(...args)

    this.id = LayerLevelControl.C_KEY
    this.label = '表示する階層'
    this.type = 'radio'
  }
}

/** 複数組織選択 */
class MultiDepartmentControl extends BaseControl {
  static C_KEY = 'department'

  constructor(...args) {
    const [id, ...rest] = args
    super(...rest)

    this.id = id || MultiDepartmentControl.C_KEY
    this.label = '組織を選択'
    this.type = 'multiselect'
  }
}

/** 組織階層に応じて選択肢が変わる複数選択組織 */
class MultiDepartmentSelectControl extends BaseControl {
  static C_KEY = 'departmentSelect'

  constructor(...args) {
    super(...args)

    this.id = MultiDepartmentSelectControl.C_KEY
    this.label = '組織を選択'
    this.type = 'multiselect'
  }
}

/** 単一資格選択 */
class SingleCertificationControl extends BaseControl {
  static C_KEY = 'scoreAnalysisCert'

  constructor(args) {
    super(args)
    this.id = SingleCertificationControl.C_KEY
    this.label = '資格を選択（単一選択）'
    this.type = 'select'
  }
}

/** 複数資格選択 */
class MultiCertificationControl extends BaseControl {
  static C_KEY = 'certificationList'

  constructor(args, groupType) {
    super(args)
    this.id = MultiCertificationControl.C_KEY
    this.label = '資格を選択'

    this.groupType = groupType
    this.state.unselectedText = '未選択'

    this.type = 'multiselect'

    this.state.showAllOption = true
    this.state.showDropdown = false
  }
}

/** メインカテゴリー選択 */
class MainCategoryControl extends BaseControl {
  static C_KEY = 'mainCategory'
  constructor(...args) {
    super(...args)
    this.id = MainCategoryControl.C_KEY
    this.label = 'メインカテゴリーを選択'
    this.state.unselectedText = '未選択'
    this.type = 'multiselect'
  }
}

/** サブカテゴリの複数選択 */
class SubCategoryControl extends BaseControl {
  static C_KEY = 'subCategory'
  constructor(...args) {
    super(...args)
    this.id = SubCategoryControl.C_KEY
    this.label = 'サブカテゴリーを選択'
    this.state.unselectedText = '未選択'
    this.type = 'multiselect'
  }
}

/** 資格セット */
class CertificationSetControl extends BaseControl {
  static C_KEY = 'certificationSet'

  constructor(...args) {
    super(...args)
    this.id = CertificationSetControl.C_KEY
    this.label = '資格セットを選択'
    this.type = 'select'

    this.state.showSearch = false
  }
}

/** 基準点数 */
class CertificationScoreControl extends BaseControl {
  static C_KEY = 'score'

  constructor(...args) {
    super(...args)
    this.id = CertificationScoreControl.C_KEY
    this.label = '点数を入力'
    this.type = 'text'
  }
}

class ScoringAggregation extends BaseControl {
  static C_KEY = 'scoringAggregation'
  constructor(...args) {
    super(...args)
    this.id = ScoringAggregation.C_KEY
    this.label = '同一資格を複数回取得している場合の基準'
    this.type = 'radio'
  }
}
